import { useEffect, useState } from 'react';
import { accountRequest } from '../../net/accounts';

interface Item {
  id: string;
  kind: string;
  name: string;
  revision: string;
  updatedAt: number;
  data: unknown;
}
export function CloudLibraryPanel({
  kind,
  current,
  onLoad,
  saveBlocked = null,
  loadBlocked = null,
}: {
  kind: 'board' | 'arena' | 'save';
  current: () => { id: string; name: string; data: unknown };
  onLoad: (data: unknown) => void;
  /**
   * Why saving or loading cannot work just now, said on the button rather than after a press
   * that could only fail (client-tablets-ui:quality#19).
   */
  saveBlocked?: string | null;
  loadBlocked?: string | null;
}) {
  const [items, setItems] = useState<Item[]>([]);
  const [selected, setSelected] = useState<Item | null>(null);
  const [notice, setNotice] = useState(
    'Sign in to save and load your private cloud copies. Browser saves remain separate.',
  );
  const [busy, setBusy] = useState(false);
  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'Cloud request failed');
    } finally {
      setBusy(false);
    }
  };
  const refresh = async () => {
    const result = await accountRequest<{ items: Item[] }>(`library?kind=${kind}`);
    setItems(result.items);
    setNotice(
      result.items.length
        ? 'Choose a revision to load it. Saving requires the latest revision.'
        : 'No cloud copies yet.',
    );
  };
  useEffect(() => {
    void run(refresh);
    // Opening the pane is the refresh action; saving refreshes it again below.
  }, [kind]);
  return (
    <section className="editor-section cloud-library" aria-label="Cloud library">
      <h3>{kind === 'save' ? 'GAME STORAGE' : `Cloud ${kind}s`}</h3>
      <div className="menu-actions">
        {kind !== 'save' && (
          <button disabled={busy} onClick={() => void run(refresh)}>
            Refresh cloud list
          </button>
        )}
        <button
          disabled={busy || saveBlocked !== null}
          title={saveBlocked ?? undefined}
          data-testid={kind === 'save' ? 'cloud-save' : undefined}
          onClick={() =>
            void run(async () => {
              const value = current();
              const result = await accountRequest<{ item: Item }>('library', {
                ...value,
                kind,
                expectedRevision: selected?.id === value.id ? selected.revision : null,
              });
              setSelected(result.item);
              await refresh();
              setNotice('Saved to your cloud account.');
            })
          }
        >
          {kind === 'save' ? 'Save Game' : 'Save to cloud'}
        </button>
      </div>
      <p role="status" className="hint">
        {notice}
      </p>
      {(saveBlocked ?? loadBlocked) && (
        <p className="hint" data-testid="cloud-blocked">
          {saveBlocked ?? loadBlocked}
        </p>
      )}
      <ul className="saved-boards">
        {items.map((item) => (
          <li key={item.revision}>
            <span>
              {item.name} · {new Date(item.updatedAt).toLocaleString()}
            </span>
            {kind === 'save' && (
              <button
                disabled={busy}
                onClick={() => {
                  setSelected(item);
                  setNotice(
                    'Selected this save slot. Save Game will replace it only if this revision is still current.',
                  );
                }}
              >
                Select save slot
              </button>
            )}
            <button
              disabled={busy || loadBlocked !== null}
              title={loadBlocked ?? undefined}
              onClick={() => {
                try {
                  onLoad(item.data);
                  setSelected(item);
                  setNotice(`Loaded ${item.name}.`);
                } catch (e) {
                  setNotice(e instanceof Error ? e.message : 'Cannot load this version');
                }
              }}
            >
              {kind === 'save' ? 'Load Game' : 'Load revision'}
            </button>
          </li>
        ))}
      </ul>
      <p className="hint">
        Up to 20 items and 20 saves daily per library; three revisions per item. Cloud saves are
        private and are not published games.
      </p>
    </section>
  );
}
